import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Filter, Trash2, X, Save, ChevronRight, MessageCircle } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { useI18n } from "../../i18n/I18nContext";
import { api, formatApiError, formatPrice } from "../../lib/api";

const STATUS_META = {
    new: { color: "bg-brand-gold text-white", label_fr: "Nouveau", label_en: "New" },
    in_progress: { color: "bg-brand-navy text-white", label_fr: "En cours", label_en: "In progress" },
    responded: { color: "bg-brand-emerald text-white", label_fr: "Répondu", label_en: "Responded" },
    closed: { color: "bg-gray-400 text-white", label_fr: "Clôturé", label_en: "Closed" },
};

export default function AdminInquiries() {
    const { t, lang } = useI18n();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", type: "" });
    const [selected, setSelected] = useState(null);
    const [draft, setDraft] = useState({ status: "new", response_text: "" });
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.type) params.type = filters.type;
        api.get("/admin/inquiries", { params })
            .then((r) => setList(r.data || []))
            .catch(() => setList([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, [filters]);

    const openDrawer = (item) => {
        setSelected(item);
        setDraft({
            status: item.status || "new",
            response_text: item.response_text || "",
        });
    };

    const saveDrawer = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/inquiries/${selected.id}`, draft);
            toast.success(t("inquiries.saved"));
            setList((prev) => prev.map((p) => (p.id === data.id ? data : p)));
            setSelected(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm(t("admin.confirm_delete"))) return;
        try {
            await api.delete(`/admin/inquiries/${id}`);
            toast.success(t("inquiries.deleted"));
            setList((prev) => prev.filter((p) => p.id !== id));
            if (selected?.id === id) setSelected(null);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const labelStatus = (s) => STATUS_META[s]?.[lang === "en" ? "label_en" : "label_fr"] || s;

    return (
        <AdminLayout>
            <div className="p-6 lg:p-10">
                <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                    <div>
                        <div className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-gold mb-2">
                            {t("admin.dashboard")}
                        </div>
                        <h1 className="font-display text-3xl text-brand-navy">{t("inquiries.title")}</h1>
                    </div>
                    <div className="flex flex-wrap items-end gap-3" data-testid="inquiries-filters">
                        <Sel
                            label={t("inquiries.filters.status")}
                            value={filters.status}
                            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                            testid="filter-inq-status"
                            options={[
                                { v: "", l: t("inquiries.filters.all") },
                                { v: "new", l: t("cockpit.new") },
                                { v: "in_progress", l: t("cockpit.in_progress") },
                                { v: "responded", l: t("cockpit.responded") },
                                { v: "closed", l: t("cockpit.closed") },
                            ]}
                        />
                        <Sel
                            label={t("inquiries.filters.type")}
                            value={filters.type}
                            onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
                            testid="filter-inq-type"
                            options={[
                                { v: "", l: t("inquiries.filters.all") },
                                { v: "info", l: t("cockpit.info") },
                                { v: "quote", l: t("cockpit.quote") },
                            ]}
                        />
                    </div>
                </div>

                <div className="bg-white border border-gray-100 overflow-x-auto" data-testid="inquiries-table">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-paper text-[0.65rem] tracking-[0.18em] uppercase text-brand-navy">
                            <tr>
                                <th className="text-left px-4 py-3">{t("inquiries.columns.date")}</th>
                                <th className="text-left px-4 py-3">{t("inquiries.columns.name")}</th>
                                <th className="text-left px-4 py-3">{t("inquiries.columns.type")}</th>
                                <th className="text-left px-4 py-3">{t("inquiries.columns.subject")}</th>
                                <th className="text-left px-4 py-3">{t("inquiries.columns.status")}</th>
                                <th className="text-left px-4 py-3">{t("inquiries.columns.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="text-center py-12 text-gray-500">Chargement…</td></tr>
                            ) : list.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-12 text-gray-500">{t("inquiries.empty")}</td></tr>
                            ) : list.map((it) => (
                                <tr key={it.id} className="border-t border-gray-100 hover:bg-brand-paper/60 transition-colors" data-testid={`inq-row-${it.id}`}>
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                                        {new Date(it.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-brand-navy">{it.name}</div>
                                        <div className="text-xs text-gray-500">{it.email}</div>
                                    </td>
                                    <td className="px-4 py-3 capitalize">
                                        <span className={`inline-block px-2 py-0.5 text-[0.62rem] tracking-[0.22em] uppercase font-bold ${it.type === "quote" ? "bg-brand-navy text-white" : "bg-brand-gold/15 text-brand-gold-deep"}`}>
                                            {it.type === "quote" ? t("cockpit.quote") : t("cockpit.info")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 max-w-sm truncate text-gray-700">
                                        {it.property_title || it.message?.slice(0, 60)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2.5 py-1 text-[0.62rem] tracking-[0.22em] uppercase font-bold ${STATUS_META[it.status]?.color || "bg-gray-300 text-white"}`}>
                                            {labelStatus(it.status)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => openDrawer(it)}
                                                data-testid={`inq-open-${it.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-navy hover:bg-brand-navy-soft text-white text-xs uppercase tracking-wide transition-colors"
                                            >
                                                {t("inquiries.details")} <ChevronRight className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={() => remove(it.id)}
                                                data-testid={`inq-delete-${it.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs uppercase tracking-wide transition-colors"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Drawer */}
            <AnimatePresence>
                {selected && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/40 z-40"
                            onClick={() => setSelected(null)}
                        />
                        <motion.aside
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 24, stiffness: 220 }}
                            className="fixed top-0 right-0 h-screen w-full sm:w-[520px] bg-white z-50 shadow-2xl overflow-y-auto"
                            data-testid="inq-drawer"
                        >
                            <header className="sticky top-0 bg-brand-navy text-white px-5 py-4 flex items-center justify-between z-10">
                                <div>
                                    <div className="text-[0.62rem] tracking-[0.24em] uppercase font-bold text-brand-gold">
                                        {selected.type === "quote" ? t("cockpit.quote") : t("cockpit.info")} · #{selected.id.slice(0, 8)}
                                    </div>
                                    <h3 className="font-display text-xl mt-0.5">{selected.name}</h3>
                                </div>
                                <button
                                    onClick={() => setSelected(null)}
                                    className="w-9 h-9 inline-flex items-center justify-center bg-white/10 hover:bg-brand-gold transition-colors"
                                    data-testid="inq-drawer-close"
                                    aria-label={t("inquiries.close_drawer")}
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </header>

                            <div className="p-5 space-y-5 pb-24">
                                <Section title={t("inquiries.client_info")}>
                                    <KV k="Email" v={<a className="text-brand-emerald hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a>} />
                                    <KV k="Téléphone" v={selected.phone || "—"} />
                                    <KV
                                        k="WhatsApp"
                                        v={
                                            (selected.whatsapp || selected.phone) ? (
                                                <a
                                                    className="inline-flex items-center gap-1 text-[#25D366] hover:underline"
                                                    href={`https://wa.me/${(selected.whatsapp || selected.phone).replace(/[^0-9]/g, "")}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <MessageCircle className="w-3.5 h-3.5" /> {selected.whatsapp || selected.phone}
                                                </a>
                                            ) : "—"
                                        }
                                    />
                                </Section>

                                <Section title={t("inquiries.project_info")}>
                                    <KV k="Bien" v={selected.property_title || "—"} />
                                    <KV k={t("inquiry.transaction")} v={selected.transaction === "sale" ? t("inquiry.transaction_sale") : selected.transaction === "rent" ? t("inquiry.transaction_rent") : "—"} />
                                    <KV k={t("inquiry.property_type")} v={selected.property_type || "—"} />
                                    <KV k={t("inquiry.city")} v={selected.city || "—"} />
                                    <KV k={t("inquiry.budget")} v={selected.budget ? formatPrice(selected.budget, "FCFA") : "—"} />
                                    <KV k={t("inquiry.timeline")} v={selected.timeline || "—"} />
                                    <div className="mt-3">
                                        <div className="text-[0.62rem] tracking-[0.22em] uppercase font-bold text-gray-500 mb-1">{t("inquiry.message")}</div>
                                        <p className="text-sm text-brand-navy whitespace-pre-line">{selected.message}</p>
                                    </div>
                                </Section>

                                <Section title={t("inquiries.update_status")}>
                                    <div className="grid grid-cols-2 gap-2" data-testid="inq-status-picker">
                                        {["new", "in_progress", "responded", "closed"].map((s) => (
                                            <button
                                                key={s}
                                                type="button"
                                                onClick={() => setDraft((d) => ({ ...d, status: s }))}
                                                data-testid={`inq-status-${s}`}
                                                className={`px-3 py-2.5 text-[0.7rem] tracking-[0.2em] uppercase font-semibold transition-all border ${
                                                    draft.status === s
                                                        ? `${STATUS_META[s].color} border-transparent`
                                                        : "bg-white text-brand-navy border-gray-200 hover:border-brand-gold"
                                                }`}
                                            >
                                                {labelStatus(s)}
                                            </button>
                                        ))}
                                    </div>
                                </Section>

                                <Section title={t("inquiries.response")}>
                                    <textarea
                                        value={draft.response_text}
                                        onChange={(e) => setDraft((d) => ({ ...d, response_text: e.target.value }))}
                                        rows={5}
                                        placeholder={t("inquiries.response_placeholder")}
                                        data-testid="inq-response-textarea"
                                        className="w-full border border-gray-200 px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors resize-y min-h-[120px]"
                                    />
                                </Section>

                                <div className="flex gap-3">
                                    <button
                                        onClick={saveDrawer}
                                        disabled={saving}
                                        data-testid="inq-save"
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.7rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                                    >
                                        <Save className="w-4 h-4" /> {saving ? "…" : t("inquiries.save")}
                                    </button>
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </AdminLayout>
    );
}

function Sel({ label, value, onChange, options, testid }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy inline-flex items-center gap-1">
                <Filter className="w-3 h-3" /> {label}
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="bg-white border border-gray-200 px-4 py-2.5 text-sm text-brand-navy focus:outline-none focus:border-brand-gold min-w-[160px]"
            >
                {options.map((o) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                ))}
            </select>
        </label>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <div className="text-[0.62rem] tracking-[0.24em] uppercase font-bold text-brand-gold mb-3">{title}</div>
            <div className="space-y-2">{children}</div>
        </section>
    );
}

function KV({ k, v }) {
    return (
        <div className="flex items-start justify-between gap-4 text-sm border-b border-gray-100 py-2">
            <span className="text-gray-500 uppercase text-[0.65rem] tracking-[0.18em] font-semibold min-w-[110px]">{k}</span>
            <span className="text-brand-navy text-right flex-1">{v}</span>
        </div>
    );
}
